import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Rating,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import api from "../api/api";

const RATING_FIELDS = [
  {
    key: "RegistrationRating",
    label: "Registration experience",
    helperText: "Rate the registration and check-in process.",
  },
  {
    key: "VenueRating",
    label: "Venue",
    helperText: "Rate the venue, seating, and accessibility.",
  },
  {
    key: "FoodRating",
    label: "Food",
    helperText: "Rate the food quality and service.",
  },
  {
    key: "ScheduleRating",
    label: "Schedule",
    helperText: "Rate the event timing and session flow.",
  },
  {
    key: "CulturalProgramRating",
    label: "Cultural program",
    helperText: "Rate the cultural activities and engagement.",
  },
  {
    key: "HospitalityRating",
    label: "Hospitality",
    helperText: "Rate the hospitality and overall support.",
  },
  {
    key: "OverallOrganizationRating",
    label: "Overall organization",
    helperText: "Rate the complete event organization.",
  },
];

const RATING_LABELS = {
  1: "Very poor",
  2: "Poor",
  3: "Average",
  4: "Good",
  5: "Excellent",
};

function buildInitialForm() {
  return {
    RegistrationRating: "",
    VenueRating: "",
    FoodRating: "",
    ScheduleRating: "",
    CulturalProgramRating: "",
    HospitalityRating: "",
    OverallOrganizationRating: "",
    Comments: "",
  };
}

function FeedbackForm() {
  const { isAuthenticated, user } = useSelector((state) => state.user);
  const location = useLocation();
  const navigate = useNavigate();
  const [form, setForm] = useState(buildInitialForm);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const token = useMemo(
    () => user?.token || user?.accessToken || user?.jwtToken || "",
    [user],
  );

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  const handleChange = (field) => (event) => {
    setForm((currentForm) => ({
      ...currentForm,
      [field]: event.target.value,
    }));
  };

  const handleRatingChange = (field) => (_, value) => {
    setForm((currentForm) => ({
      ...currentForm,
      [field]: value || "",
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccessMessage("");

    if (!token) {
      setError("Logged-in session token is missing. Please sign in again.");
      return;
    }

    const hasMissingRatings = RATING_FIELDS.some(({ key }) => !form[key]);
    if (hasMissingRatings) {
      setError("Please rate all feedback sections before submitting.");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = RATING_FIELDS.reduce(
        (accumulator, { key }) => ({
          ...accumulator,
          [key]: Number(form[key]),
        }),
        {
          Comments: form.Comments.trim(),
        },
      );

      await api.createFeedback(payload, token);
      setSuccessMessage("Feedback submitted successfully.");
      setForm(buildInitialForm());
    } catch (submitError) {
      setError(
        submitError?.response?.data?.message ||
          "Unable to submit feedback right now.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 4, md: 7 },
        background:
          "linear-gradient(180deg, #f8fafc 0%, #edf4ff 45%, #ffffff 100%)",
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4} alignItems="stretch">
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper
              elevation={0}
              sx={{
                height: "100%",
                p: { xs: 3, md: 4 },
                borderRadius: 4,
                color: "white",
                background:
                  "linear-gradient(145deg, #0f172a 0%, #1d4ed8 55%, #38bdf8 100%)",
                boxShadow: "0 24px 50px rgba(29, 78, 216, 0.18)",
              }}
            >
              <Stack spacing={2.5}>
                <Typography variant="overline" sx={{ letterSpacing: 2 }}>
                  Alumni Feedback
                </Typography>
                <Typography variant="h4" fontWeight={800}>
                  Share your event experience
                </Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.86)" }}>
                  Your feedback helps improve registration, hospitality, event
                  planning, and the overall alumni experience for future
                  gatherings.
                </Typography>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    bgcolor: "rgba(255,255,255,0.12)",
                    color: "white",
                    border: "1px solid rgba(255,255,255,0.16)",
                  }}
                >
                  <CardContent>
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                      Rating scale
                    </Typography>
                    <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)" }}>
                      1 means very poor and 5 means excellent.
                    </Typography>
                  </CardContent>
                </Card>
              </Stack>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 8 }}>
            <Paper
              component="form"
              onSubmit={handleSubmit}
              elevation={0}
              sx={{
                p: { xs: 3, sm: 4, md: 5 },
                borderRadius: 4,
                border: "1px solid rgba(148, 163, 184, 0.18)",
                boxShadow: "0 24px 60px rgba(15, 23, 42, 0.08)",
              }}
            >
              <Stack spacing={3}>
                <Box>
                  <Typography variant="h4" fontWeight={800} color="#0f172a">
                    Event feedback form
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    All registered users can submit this form. Guests are redirected
                    to the login page automatically.
                  </Typography>
                </Box>

                {error ? <Alert severity="error">{error}</Alert> : null}
                {successMessage ? (
                  <Alert severity="success">{successMessage}</Alert>
                ) : null}

                <Grid container spacing={2.5}>
                  {RATING_FIELDS.map((field) => (
                    <Grid key={field.key} size={{ xs: 12, sm: 6 }}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2.25,
                          height: "100%",
                          borderRadius: 3,
                          border: "1px solid rgba(148, 163, 184, 0.22)",
                          bgcolor: "#f8fafc",
                        }}
                      >
                        <Stack spacing={1.25}>
                          <Typography variant="subtitle1" fontWeight={700} color="#0f172a">
                            {field.label}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {field.helperText}
                          </Typography>
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1}
                            alignItems={{ xs: "flex-start", sm: "center" }}
                          >
                            <Rating
                              name={field.key}
                              value={Number(form[field.key]) || null}
                              onChange={handleRatingChange(field.key)}
                              max={5}
                              size="large"
                              sx={{ color: "#f59e0b" }}
                            />
                            <Typography variant="body2" fontWeight={600} color="#334155">
                              {form[field.key]
                                ? `${form[field.key]}/5 - ${RATING_LABELS[form[field.key]]}`
                                : "Select a rating"}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Paper>
                    </Grid>
                  ))}

                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={5}
                      label="Comments"
                      placeholder="Share suggestions, highlights, or anything that can help improve future events."
                      value={form.Comments}
                      onChange={handleChange("Comments")}
                    />
                  </Grid>
                </Grid>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  justifyContent="space-between"
                  alignItems={{ xs: "stretch", sm: "center" }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Signed in as {user?.Email || user?.email || "member"}.
                  </Typography>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    <Button
                      type="button"
                      variant="outlined"
                      onClick={() => navigate(-1)}
                      disabled={isSubmitting}
                      sx={{ textTransform: "none", borderRadius: 999 }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isSubmitting}
                      sx={{ textTransform: "none", borderRadius: 999, px: 3 }}
                    >
                      {isSubmitting ? (
                        <CircularProgress size={22} color="inherit" />
                      ) : (
                        "Submit feedback"
                      )}
                    </Button>
                  </Stack>
                </Stack>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default FeedbackForm;
